/*    
<summary>
  This functional component "AddProductManagementForm" provides form to add new user or update existing user.
  Developer:Sahil Bharti, Created Date:06-January-2025
</summary>
<returns>Returns JSX</returns>
*/

import { useCallback, useEffect, useState } from "react";
import { Accordion, Button, Modal } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import { useStore } from "../../../contexts/store-provider";
import { ICategoryProduct } from "../../../models/state/IStoreProduct";
import LoaderButton from "../../../shared-components/button/loader-button";
import CategoryProductItems from "../components/category-product-items";
import { observer } from "mobx-react";
import SlidingPanel from "react-sliding-side-panel";
import { formatMessage } from "../../../translations/format-message";
import { IAddSaleItemVM } from "../../../models/response/ISMRResponse";
import { serviceTypeEnum } from "../../../constants/enums/service-type-enum";

interface IProps {
  storeId: number;
  formValues: any;
  openPanel: boolean;
  saleItemIds: number[];
  submitHandler: (values: any) => void;
  modalClosed: () => void;
}

const AddProductForm = (props: IProps) => {
  const {
    preferencesStore,
    masterManagementStore,
    categoryManagementStore,
    productManagementStore,
  } = useStore();
  const {
    GetCategoryListByStoreIdForProductService,
    allCategoryForProductStore,
  } = categoryManagementStore;
  const { islanguageChanged, updateLanguageChangedFlag } = preferencesStore;
  const { getProductData, GetProductListByCatIdService } =
    productManagementStore;
  const [openAccordions, setOpenAccordions] = useState<Set<string>>(new Set());
  const { storeId, saleItemIds } = props;
  const [selectedProduct, setSelectedProduct] = useState<any[]>([]);
  const [err, setErr] = useState<boolean>(false);

  // Updated state for recursive categories
  const [categories, setCategories] = useState<ICategoryProduct[]>(
    allCategoryForProductStore
  );

  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(false);
  // Transform the flat category data into a hierarchical structure when received from API
  useEffect(() => {
    // Transform the flat category data into a hierarchical structure
    setCategories(allCategoryForProductStore);
  }, [allCategoryForProductStore]);

  /**
   * This function calls the "getAllGroup" store funtion that get configuration list from API
   */
  const callGetProductManagementListService = useCallback(() => {
    return GetCategoryListByStoreIdForProductService(2, storeId);
  }, [GetCategoryListByStoreIdForProductService, storeId]);

  useEffect(() => {
    callGetProductManagementListService();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callGetProductManagementListService]);

  useEffect(() => {
    if (islanguageChanged) {
      callGetProductManagementListService().then(() => {
        refreshProductsForOpenAccordions();
      });
      updateLanguageChangedFlag();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [islanguageChanged]);

  const findCategoryById = useCallback(
    (categories: ICategoryProduct[], id: string): ICategoryProduct | null => {
      for (const category of categories) {
        if (category.categoryId === id) return category;
        if (category.subCategories && category.subCategories.length > 0) {
          const found = findCategoryById(category.subCategories, id);
          if (found) return found;
        }
      }
      return null;
    },
    []
  );

  const refreshProductsForOpenAccordions = useCallback(async () => {
    if (openAccordions.size === 0) return;

    const fetchPromises = Array.from(openAccordions).map(async (eventKey) => {
      const [, index] = eventKey.split("-");
      const categoryIndex = Number(index);

      if (categories.length > categoryIndex) {
        const categoryId = categories[categoryIndex].categoryId;
        try {
          await GetProductListByCatIdService(Number(categoryId), storeId, true);
          const products = getProductData.get(Number(categoryId)) || [];
          return { categoryId, products };
        } catch (error) {
          console.error(
            `Error refreshing products for category ${categoryId}:`,
            error
          );
          return { categoryId, products: [] }; // Return empty array on error
        }
      }
      return null;
    });

    const results = await Promise.all(fetchPromises);

    setCategories((prevCategories) => {
      const updatedCategories = [...prevCategories];

      results.forEach((result) => {
        if (result) {
          const categoryIndex = updatedCategories.findIndex(
            (cat) => cat.categoryId === result.categoryId
          );

          if (categoryIndex !== -1) {
            updatedCategories[categoryIndex] = {
              ...updatedCategories[categoryIndex],
              products: result.products,
            };
          }
        }
      });

      return updatedCategories;
    });
  }, [
    GetProductListByCatIdService,
    categories,
    storeId,
    getProductData,
    openAccordions,
  ]);

  const findCategoryInSubcategories = useCallback(
    (subCategories: ICategoryProduct[], productId: number): string | null => {
      for (const subCategory of subCategories) {
        if (subCategory.products?.some((t) => t.Id === productId)) {
          return subCategory.categoryId;
        }
        const found = findCategoryInSubcategories(
          subCategory.subCategories,
          productId
        );
        if (found) return found;
      }
      return null;
    },
    []
  );

  const onSubmit = () => {
    if (selectedProduct.length > 0) {
      setErr(false); // Reset error state
      let productArray: IAddSaleItemVM[] = [];
      selectedProduct?.forEach((product: any) => {
        productArray.push({
          ItemType: serviceTypeEnum.Product,
          ItemId: product?.Id,
          ItemName: product?.ProductName,
          Quantity: 1,
          Price: product?.PriceIncludingTax,
          Tax: product.TaxValue,
          TaxAmount: product.TaxAmount,
          DiscountType: 0,
          DiscountCash: 0,
          DiscountPercent: 0,
          StaffId: parseInt(props?.formValues?.StaffId),
          IsNominationApplied: props?.formValues?.IsNominationApplied,
          TimeValue: null,
        });
      });

      props.submitHandler(productArray);
      setSelectedProduct([]);
    props.modalClosed();
    }
    else{
      if(saleItemIds.length === 0){
        setErr(true);
        setTimeout(() => {
          setErr(false);
        }, 3000);
      }
    }
  };

  return (
    <>
      <SlidingPanel
        type={"right"}
        isOpen={props.openPanel}
        noBackdrop={false}
        size={100}
      >
        <div className="bg_slide panel">
          <div className="row bb p-2 pl-4 g-0 align-items-center ">
            <div className="col-10">
              <h2 className="fw-bold mb-0 mt-1 py-2">
                <FormattedMessage id="add_merchandise" />
              </h2>
            </div>
            <div className="col-2">
              <span title={formatMessage("close")}>
                <svg
                  onClick={props.modalClosed}
                  role="button"
                  xlinkTitle="close"
                  xmlns="http://www.w3.org/2000/svg"
                  width="30"
                  height="30"
                  fill="currentColor"
                  className="bi bi-x ms-auto d-block"
                  viewBox="0 0 16 16"
                >
                  <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
                </svg>
              </span>
            </div>
          </div>
          <div className="sidebar_booking">
            <div  className="group_detailsWrapper">
            {categories.length > 0 ? (
              <Accordion alwaysOpen>
                <div className="choose-menu-section mb-4 p-2">
                  {categories?.map((category, index) => {
                    return (
                      <CategoryProductItems
                        key={category.categoryId}
                        category={category}
                        index={index}
                        parentId={null}
                        onDetailClick={(productDetail) => {
                          // setFieldValue("SelectedProduct", productDetail);
                        }}
                        setCategories={setCategories}
                        storeId={storeId}
                        openAccordions={openAccordions}
                        setOpenAccordions={setOpenAccordions}
                        isDisabled={isButtonDisabled}
                        selectedProduct={selectedProduct}
                        saleItemIds={saleItemIds}
                        setSelectedProduct={setSelectedProduct}
                      />
                    );
                  })}
                </div>
              </Accordion>
            ) : (
              <div className="center-content">
                <FormattedMessage id="no_category" />
              </div>
            )}
            {err && (
              <span
                data-testid="testError"
                className="text-danger errorMessage d-flex justify-content-center my-3"
              >
                <FormattedMessage
                  id="select_atleast_one"
                />
              </span>
            )}
            </div>
            {/* Footer Buttons */}
            <div className="modal-footer center-content">
                <Button
                  variant="outline-danger"
                  type="button"
                  onClick={props.modalClosed}
                >
                  <i className="bi bi-x-lg me-1"></i>
                  <FormattedMessage id="close" />
                </Button>
                <LoaderButton
                  type="button"
                  iconName="bi-check-lg"
                  disabled={selectedProduct === undefined}
                  onClick={onSubmit}
                  text="add"
                />
              </div>
          </div>
        </div>
      </SlidingPanel>
    </>
  );
};

export default observer(AddProductForm);
